import { API_BASE } from '../apiBase';
import BillToPicker from '../lib/BillToPicker';
import { fetchOrgAddressMap, billingAddressFor } from '../lib/orgAddress';
import { fmtPhone, fmtPhoneInput } from '../lib/phone';
// src/billing/CreateInvoice.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import QuickCreateProject from './QuickCreateProject';

function CreateInvoice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id'); // present when editing an existing invoice

  // Resume the in-progress draft ONLY when arriving fresh — editing (?id=) OR
  // arriving with explicit intent (?project= / ?fromQuote= / ?fromProposal=)
  // must start clean. The stale draft's project once outvoted the project the
  // user explicitly came from and numbered an invoice against the WRONG PO.
  const arrivedWithIntent = !!(editId || searchParams.get('project') || searchParams.get('fromQuote') || searchParams.get('fromProposal'));
  const savedDraft = arrivedWithIntent ? {} : (JSON.parse(localStorage.getItem('draftInvoice')) || {});

  const [projectSearch, setProjectSearch] = useState(savedDraft.projectSearch || '');
  const [selectedProject, setSelectedProject] = useState(savedDraft.selectedProject || null);
  // WHICH PERSON this invoice bills (DMV ticket-closers etc.) — null = the
  // project's own customer, exactly as before.
  const [billToCust, setBillToCust] = useState(null);
  // relationship addresses — print-time fallback when a person has none
  const [orgAddrs, setOrgAddrs] = useState(new Map());
  useEffect(() => { fetchOrgAddressMap().then(setOrgAddrs); }, []);
  // Inline "quick create project" — shared card (see QuickCreateProject.js)
  const [quickProjOpen, setQuickProjOpen] = useState(false);
  const [quickProjSeed, setQuickProjSeed] = useState('');
  const openQuickProject = () => {
    setQuickProjSeed(projectSearch.trim());
    setProjectSearch('');
    setQuickProjOpen(true);
  };
  const [currency, setCurrency] = useState(savedDraft.currency || 'USD');
  const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);
  const [lineItems, setLineItems] = useState(savedDraft.lineItems || [{ id: generateId(), description: '', quantity: 1, unitPrice: 0 }]);
  // dueInDays = number of days from the invoice date until payment is due (was
  // the old "valid for" field, repurposed). Default 7. showDueDate prints the
  // computed due date; showLateFee prints the 1.5%/month late-payment note.
  const [validUntil, setValidUntil] = useState(savedDraft.validUntil || '7');
  const [memo, setMemo] = useState(savedDraft.memo || '');
  const [showSignature, setShowSignature] = useState(savedDraft.showSignature ?? false);
  const [showPayLink, setShowPayLink] = useState(savedDraft.showPayLink ?? false);
  const [showCardFee, setShowCardFee] = useState(savedDraft.showCardFee ?? false); // +3.5% card total line
  const [showValidFor, setShowValidFor] = useState(savedDraft.showValidFor ?? false);
  const [showLateFee, setShowLateFee] = useState(savedDraft.showLateFee ?? true);
  const [showThankYou, setShowThankYou] = useState(savedDraft.showThankYou ?? true);
  // Output-column toggles: show the Qty / Unit Price columns on the PDF (default off).
  const [showQtyCol, setShowQtyCol] = useState(savedDraft.showQtyCol ?? false);
  const [showUnitPriceCol, setShowUnitPriceCol] = useState(savedDraft.showUnitPriceCol ?? false);
  const [isLumpSum, setIsLumpSum] = useState(savedDraft.isLumpSum ?? true); // hide per-line amounts (lump sum) — default ON
  // A sent invoice is view-only: no edits, no re-save. Delete + recreate to change.
  const [savedStatus, setSavedStatus] = useState('Draft');
  const [savedSources, setSavedSources] = useState({}); // lineage carried into revisions
  // Sent invoices are view-only — except for SUPER ADMIN, who can always
  // re-save (Sent, Revised, Paid, anything). Everyone else gets the lock.
  const isSuperAdminUser = localStorage.getItem('role') === 'superadmin';
  const sentLocked = savedStatus !== 'Draft' && !isSuperAdminUser;
  // Click any value in the Selected Project card to copy it to the clipboard.
  const [copied, setCopied] = useState('');
  const renderCopy = (id, value) => {
    const text = String(value == null ? '' : value).trim();
    return (
      <span
        onClick={() => { if (text && navigator.clipboard) { navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(''), 1200); } }}
        title={text ? 'Click to copy' : ''}
        style={{ cursor: text ? 'pointer' : 'default', borderBottom: text ? '1px dotted #9bb0c9' : 'none' }}
      >
        {value || 'N/A'}{copied === id ? '  ✓ copied' : ''}
      </span>
    );
  };
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // For natural typing in unit price field
  const [focusedPriceIndex, setFocusedPriceIndex] = useState(null);
  const [rawPriceInput, setRawPriceInput] = useState('');

  // Ref for focusing Description inputs
  const descriptionRefs = useRef([]);

  const currentYearShort = new Date().getFullYear().toString().slice(-2);

  // NEW: Store invoices for the currently selected project only
  const [projectInvoices, setProjectInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Success toast — mirrors the Quote flow: confirm without redirecting, so the
  // invoice you just saved stays on screen (number doesn't bump to the next one).
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Dedup: once an invoice is saved we remember its DB id and number, so a
  // following "Save & Send" UPDATES that same record instead of creating a
  // duplicate. A brand-new variant (-002, -003…) starts only after Clear.
  // Restore the edit identity from the draft too, so leaving and returning to
  // this page keeps you on the SAME invoice (number stays locked, save updates
  // it) instead of bumping to the next number / creating a duplicate.
  const [currentInvoiceId, setCurrentInvoiceId] = useState(savedDraft.currentInvoiceId || null);
  const [savedNumber, setSavedNumber] = useState(savedDraft.savedNumber || null);
  const [isSaving, setIsSaving] = useState(false);

// ==================== DYNAMIC INVOICE NUMBER ====================
  // Manual override — for recreating an invoice under a specific number.
  // Blank = automatic numbering.
  const [numberOverride, setNumberOverride] = useState('');
  // Super-admin in-place edit of the Selected card (this document only).
  const [cardForm, setCardForm] = useState(null);
  const openCardEdit = () => setCardForm({
    number: invoiceNumber,
    poNumber: selectedProject.poNumber || '',
    title: selectedProject.title || '',
    name: selectedProject.customer?.name || '',
    businessName: selectedProject.customer?.businessName || '',
    phone: selectedProject.customer?.primaryPhone || '',
    email: selectedProject.customer?.primaryEmail || '',
    street: selectedProject.jobSiteAddress?.street || '',
    city: selectedProject.jobSiteAddress?.city || '',
    state: selectedProject.jobSiteAddress?.state || 'CA',
    zip: selectedProject.jobSiteAddress?.zip || '',
  });
  const applyCardEdit = () => {
    if (cardForm.number.trim() && cardForm.number.trim() !== invoiceNumber) setNumberOverride(cardForm.number.trim());
    setSelectedProject({
      ...selectedProject,
      poNumber: cardForm.poNumber.trim() || selectedProject.poNumber,
      title: cardForm.title,
      customer: { ...(selectedProject.customer || {}), name: cardForm.name, businessName: cardForm.businessName, primaryPhone: cardForm.phone, primaryEmail: cardForm.email },
      jobSiteAddress: { ...(selectedProject.jobSiteAddress || {}), street: cardForm.street, city: cardForm.city, state: cardForm.state, zip: cardForm.zip },
    });
    setCardForm(null);
  };
  const ci = { padding: '3px 8px', border: '1px solid #ccd0d5', borderRadius: '5px', fontSize: '13px', margin: '2px 0 2px 6px' };
  const invoiceNumber = useMemo(() => {
    if (numberOverride.trim()) return numberOverride.trim(); // manual override wins
    // Once saved, keep the number we committed so re-saving the same invoice
    // doesn't bump to the next counter (which would create a phantom variant).
    if (savedNumber) return savedNumber;
    if (!selectedProject?.poNumber) return 'INV--XXXX-001 (select project)';

    const counters = projectInvoices
      .map(inv => {
        const match = inv.number.match(/-(\d{3})$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter(n => !isNaN(n));

    const maxCounter = counters.length > 0 ? Math.max(...counters) : 0;
    const nextCounter = String(maxCounter + 1).padStart(3, '0');

    return `INV${currentYearShort}-${selectedProject.poNumber}-${nextCounter}`;
  }, [selectedProject, projectInvoices, currentYearShort, savedNumber, numberOverride]);
  // =================================================================
// NEW: Custom Invoice Date with today as default (override ability)
  const [invoiceDate, setInvoiceDate] = useState(savedDraft.invoiceDate || new Date().toISOString().split('T')[0]);

// NEW: Auto-save to LocalStorage whenever any form field changes
  useEffect(() => {
    const draft = {
      projectSearch, selectedProject, currency, lineItems, validUntil, memo, showSignature, showPayLink, showValidFor, showLateFee, invoiceDate, showThankYou, showQtyCol, showUnitPriceCol, isLumpSum, showCardFee,
      currentInvoiceId, savedNumber // keep the edit identity so the number stays locked across navigation
    };
    localStorage.setItem('draftInvoice', JSON.stringify(draft));
  }, [projectSearch, selectedProject, currency, lineItems, validUntil, memo, showSignature, showPayLink, showValidFor, showLateFee, invoiceDate, showThankYou, showQtyCol, showUnitPriceCol, isLumpSum, showCardFee, currentInvoiceId, savedNumber]);

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear this invoice? All unsaved changes will be lost.')) {
      localStorage.removeItem('draftInvoice'); // Wipe the cache memory

      setCurrentInvoiceId(null);   // start a fresh invoice identity
      setSavedNumber(null);        // let the number recompute (next variant)
      setSelectedProject(null);
      setProjectSearch('');
      setLineItems([{ id: generateId(), description: '', quantity: 1, unitPrice: 0 }]);
      setMemo('');
      setShowValidFor(false);
      setShowPayLink(false); 
      setShowSignature(false);
      setShowThankYou(true); 
      setValidUntil('30');
      setInvoiceDate(new Date().toISOString().split('T')[0]);
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      const token = localStorage.getItem('token');
      if (!token) return navigate('/login');
      try {
        setLoadingProjects(true);
        setFetchError(null);
        const res = await axios.get(`${API_BASE}/projects`, {
          headers: { Authorization: token },
        });
        setProjects(res.data);
      } catch (err) {
        console.error('Fetch projects error:', err);
        setFetchError('Failed to load projects: ' + (err.response?.data?.error || err.message));
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [navigate]);

  // Arriving from a bid drawer's "Invoice this" button (?fromQuote= or
  // ?fromProposal=): pull the accepted paper and prefill the invoice —
  // project, line items, memo — so follow-up billing is one click.
  useEffect(() => {
    const fromQuote = searchParams.get('fromQuote');
    const fromProposal = searchParams.get('fromProposal');
    if ((!fromQuote && !fromProposal) || editId) return;
    const token = localStorage.getItem('token');
    const load = async () => {
      try {
        if (fromQuote) {
          const { data: q } = await axios.get(`${API_BASE}/quotes/${fromQuote}`, { headers: { Authorization: token } });
          const proj = q.project ? { ...q.project, customer: q.customer || q.project.customer } : null;
          if (proj) setSelectedProject(proj);
          else alert('This quote has no project yet (bid not won) — pick the project manually.');
          if (Array.isArray(q.lineItems) && q.lineItems.length) {
            setLineItems(q.lineItems.map((i) => ({ id: generateId(), description: i.description || '', quantity: Number(i.quantity ?? 1), unitPrice: Number(i.unitPrice ?? 0) })));
          }
          setMemo(`Per accepted Quote ${q.number || ''}`.trim());
        } else {
          const { data: p } = await axios.get(`${API_BASE}/proposals/${fromProposal}`, { headers: { Authorization: token } });
          const pid = p.project?._id || p.project;
          if (pid) {
            // proposals populate project thinly — fetch the full record so the
            // invoice has the customer, addresses, etc.
            const { data: proj } = await axios.get(`${API_BASE}/projects/${pid}`, { headers: { Authorization: token } });
            if (proj) setSelectedProject(proj);
          } else {
            alert('This proposal has no project yet (bid not won) — pick the project manually.');
          }
          if (Array.isArray(p.lineItems) && p.lineItems.length) {
            setLineItems(p.lineItems.map((i) => ({ id: generateId(), description: i.description || '', quantity: Number(i.qty ?? 1), unitPrice: Number(i.unitPrice ?? 0) })));
          }
          setMemo(`Per accepted Proposal ${p.number || ''} — labor only.`.trim());
        }
      } catch (e) {
        alert('Could not prefill from the quote/proposal: ' + (e.response?.data?.error || e.message));
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arriving from a project's Revenue tab (?project=) — preselect that project.
  const prefillProject = searchParams.get('project');
  useEffect(() => {
    if (!prefillProject || editId || projects.length === 0 || selectedProject) return;
    const proj = projects.find((p) => p._id === prefillProject);
    if (proj) setSelectedProject(proj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  // EDIT MODE: when arriving with ?id=, load that invoice into the form. Setting
  // currentInvoiceId + savedNumber makes the next save UPDATE this record (no
  // duplicate) and keeps its original number.
  useEffect(() => {
    if (!editId) return;
    const loadInvoice = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data: inv } = await axios.get(`${API_BASE}/invoices/${editId}`, { headers: { Authorization: token } });
        // inv.project is populated, but its customer is only an id — attach the
        // populated inv.customer so the form/preview has full customer details.
        // inv.project.customer is only an id; inv.customer is populated. When
        // they're the same person, use the populated copy as the project's
        // customer (as before). When they DIFFER, the invoice was billed to a
        // different person — keep the project anchor and set the override.
        const projCustRaw = inv.project ? inv.project.customer : null;
        const samePerson = !projCustRaw || !inv.customer || String(projCustRaw?._id || projCustRaw) === String(inv.customer._id);
        const proj = inv.project
          ? { ...inv.project, customer: samePerson ? (inv.customer || projCustRaw) : (typeof projCustRaw === 'object' ? projCustRaw : { _id: projCustRaw }) }
          : null;
        if (proj) setSelectedProject(proj);
        setBillToCust(samePerson ? null : inv.customer);
        setCurrentInvoiceId(inv._id);
        setSavedNumber(inv.number);
        setSavedStatus(inv.status || 'Draft');
        setSavedSources({ sourceQuote: inv.sourceQuote || undefined, sourceProposal: inv.sourceProposal || undefined });
        setCurrency(inv.currency || 'USD');
        if (Array.isArray(inv.lineItems) && inv.lineItems.length) {
          setLineItems(inv.lineItems.map((i) => ({
            id: i.id || generateId(),
            description: i.description || '',
            quantity: i.quantity ?? 1,
            unitPrice: i.unitPrice ?? 0,
          })));
        }
        setMemo(inv.memo || '');
        if (inv.invoiceDate) setInvoiceDate(new Date(inv.invoiceDate).toISOString().split('T')[0]);
        if (typeof inv.showSignature === 'boolean') setShowSignature(inv.showSignature);
        if (typeof inv.showPayLink === 'boolean') setShowPayLink(inv.showPayLink);
        if (typeof inv.showCardFee === 'boolean') setShowCardFee(inv.showCardFee);
        if (typeof inv.showValidFor === 'boolean') setShowValidFor(inv.showValidFor);
        if (typeof inv.showLateFee === 'boolean') setShowLateFee(inv.showLateFee);
        if (typeof inv.isLumpSum === 'boolean') setIsLumpSum(inv.isLumpSum);
        if (typeof inv.showQtyCol === 'boolean') setShowQtyCol(inv.showQtyCol);
        if (typeof inv.showUnitPriceCol === 'boolean') setShowUnitPriceCol(inv.showUnitPriceCol);
      } catch (err) {
        alert('Failed to load invoice for editing: ' + (err.response?.data?.error || err.message));
      }
    };
    loadInvoice();
  }, [editId]);

  // NEW: When a project is selected, fetch only its invoices
  useEffect(() => {
    if (!selectedProject?._id) {
      setProjectInvoices([]);
      return;
    }

    const fetchProjectInvoices = async () => {
      setLoadingInvoices(true);
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(
          `${API_BASE}/invoices?project=${selectedProject._id}`,
          { headers: { Authorization: token } }
        );
        setProjectInvoices(res.data || []);
      } catch (err) {
        console.error('Failed to fetch project invoices:', err);
        setProjectInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };

    fetchProjectInvoices();
  }, [selectedProject]);

  // Informal Estimate Log Notes for the selected project — powers the
  // "+ Add item from Informal Estimates" picker. Non-fatal if absent.
  const [projectInformalEstimates, setProjectInformalEstimates] = useState([]);
  const [showIePicker, setShowIePicker] = useState(false);
  // Which line item came from which estimate. An estimate is grayed out in
  // the picker only while its line item is STILL on the document — remove or
  // clear the line and the estimate un-grays automatically.
  const [ieLinks, setIeLinks] = useState({}); // lineItemId -> estimateId
  const addedIeIds = lineItems.map(li => ieLinks[li.id]).filter(Boolean);
  useEffect(() => {
    setShowIePicker(false);
    setIeLinks({});
    if (!selectedProject?._id) { setProjectInformalEstimates([]); return; }
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/projects/${selectedProject._id}/informal-estimates`, { headers: { Authorization: token } })
      .then(r => setProjectInformalEstimates(r.data || []))
      .catch(() => setProjectInformalEstimates([]));
  }, [selectedProject]);
  const addItemFromInformalEstimate = (ie) => {
    if (addedIeIds.includes(ie._id)) return; // already on the document
    const item = { id: generateId(), description: ie.description || '', quantity: 1, unitPrice: Number(ie.amount) || 0 };
    setLineItems(prev => {
      // Replace a single still-blank row instead of stacking under it.
      const blank = prev.length === 1 && !prev[0].description.trim() && !Number(prev[0].unitPrice);
      return blank ? [item] : [...prev, item];
    });
    setIeLinks(prev => ({ ...prev, [item.id]: ie._id }));
    setShowIePicker(false);
  };

const filteredProjects = useMemo(() => {
    if (!projectSearch) return [];
    const searchLower = projectSearch.toLowerCase();
    
    return projects.filter(
      (p) =>
        p.poNumber?.toLowerCase().includes(searchLower) ||
        p.title?.toLowerCase().includes(searchLower) ||
        p.customer?.name?.toLowerCase().includes(searchLower)
    );
  }, [projects, projectSearch]);

  const addLineItem = () => setLineItems([...lineItems, { id: generateId(), description: '', quantity: 1, unitPrice: 0 }]);

  const updateItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const removeItem = (index) => {
    // Last remaining row: X clears it instead of removing (there's always one row).
    if (lineItems.length > 1) setLineItems(lineItems.filter((_, i) => i !== index));
    else setLineItems([{ id: generateId(), description: '', quantity: 1, unitPrice: 0 }]);
  };

  const subtotal = lineItems.reduce((sum, i) => sum + (Number(i.quantity) * Number(i.unitPrice)), 0);

  // Payment due date = invoice date + N days (parsed as a LOCAL date so the
  // displayed day doesn't shift across time zones).
  const dueDate = (() => {
    const parts = String(invoiceDate || '').split('-');
    const base = parts.length === 3
      ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
      : new Date();
    base.setDate(base.getDate() + (Number(validUntil) || 0));
    return base;
  })();
  const dueDateStr = dueDate.toLocaleDateString();
  const LATE_FEE_NOTE = 'Late fee: A fee of $25 (or 1.5% of the balance, whichever is greater) applies immediately if payment is not received by the due date, and recurs monthly until paid in full.';

  // ===== LIVE PDF PREVIEW (the preview IS the real jsPDF) =====
  // The actual PDF is regenerated (debounced) whenever the form changes, so what
  // you see is exactly what downloads/sends — no separate HTML preview to keep
  // in sync, and pagination is always 100% accurate.
  const [livePdfUrl, setLivePdfUrl] = useState(null);
  useEffect(() => {
    // Always render a live preview — even with no project selected — so the
    // user sees a default mockup invoice (Client Name, "INV--XXXX-001 (select
    // project)", placeholder address). buildPDF() is fully null-safe.
    const t = setTimeout(() => {
      try {
        const url = buildPDF().output('bloburl');
        setLivePdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      } catch (e) {
        // ignore transient render errors mid-typing
      }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, billToCust, lineItems, currency, memo, showSignature, showPayLink, showCardFee, showValidFor, showLateFee, showThankYou, showQtyCol, showUnitPriceCol, isLumpSum, invoiceDate, validUntil, invoiceNumber]);

  const handleDescriptionKeyDown = (e, index) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (lineItems[index].description.trim() !== '') {
        addLineItem();
        setTimeout(() => {
          if (descriptionRefs.current[index + 1]) {
            descriptionRefs.current[index + 1].focus();
          }
        }, 0);
      }
    }
  };

  // Drag-to-reorder line items — custom POINTER drag (matches CreateQuote): a real
  // floating copy of the row follows the cursor, a dashed bar marks the drop gap,
  // and the row lands in place on release with no browser "snap-back".
  const [drag, setDrag] = useState(null);           // { fromIndex, w, h, dx, dy } while dragging
  const [pointer, setPointer] = useState(null);     // { x, y } current cursor position
  const [dropIndex, setDropIndex] = useState(null); // insertion gap 0..length
  const rowRefs = useRef([]);
  const dragRef = useRef(null);                     // mirror for the window pointerup handler

  const moveItem = (from, insertIndex) => {
    if (from == null || insertIndex == null) return;
    if (insertIndex === from || insertIndex === from + 1) return; // dropped into its own gap → no-op
    const arr = [...lineItems];
    const [moved] = arr.splice(from, 1);
    const dest = from < insertIndex ? insertIndex - 1 : insertIndex; // removal above shifts the gap down one
    arr.splice(dest, 0, moved);
    setLineItems(arr);
  };

  // Which gap (0..length) the cursor is over, ignoring the row being dragged.
  const computeDropIndex = (clientY, fromIndex) => {
    for (let i = 0; i < lineItems.length; i++) {
      if (i === fromIndex) continue;
      const el = rowRefs.current[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientY < r.top + r.height / 2) return i;
    }
    return lineItems.length;
  };

  const startRowDrag = (e, index) => {
    e.preventDefault();
    const el = rowRefs.current[index];
    if (!el) return;
    const r = el.getBoundingClientRect();
    dragRef.current = { fromIndex: index, dropIndex: index };
    setDrag({ id: lineItems[index].id, fromIndex: index, w: r.width, h: r.height, dx: e.clientX - r.left, dy: e.clientY - r.top });
    setPointer({ x: e.clientX, y: e.clientY });
    setDropIndex(index);
    const onMove = (ev) => {
      setPointer({ x: ev.clientX, y: ev.clientY });
      const idx = computeDropIndex(ev.clientY, index);
      dragRef.current.dropIndex = idx;
      setDropIndex(idx);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const d = dragRef.current;
      if (d) moveItem(d.fromIndex, d.dropIndex);
      dragRef.current = null;
      setDrag(null); setPointer(null); setDropIndex(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // A dashed indigo bar that opens at the gap where the row will drop.
  const DropBar = () => (
    <div style={{ height: '44px', marginBottom: '12px', borderRadius: '6px', border: '2px dashed #818cf8', background: 'rgba(99,102,241,0.15)', pointerEvents: 'none' }} />
  );

  const formatPrice = (value) => {
    if (value === '' || value == null || isNaN(value)) return '0.00';
    return Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const displayUnitPrice = (index, value) => {
    if (focusedPriceIndex === index) {
      return rawPriceInput;
    }
    return formatPrice(value);
  };

  // ==================== SAVE (dedup-aware) ====================
  const buildPayload = (status, customerIdOverride) => ({
    number: invoiceNumber,
    project: selectedProject._id,
    customer: customerIdOverride || billToCust?._id || selectedProject.customer?._id,
    // lineage — set when this invoice was born from a quote/proposal
    sourceQuote: searchParams.get('fromQuote') || undefined,
    sourceProposal: searchParams.get('fromProposal') || undefined,
    currency,
    lineItems: lineItems
      .filter((i) => i.description.trim())
      .map((item) => ({
        ...item,
        amount: Number(item.quantity) * Number(item.unitPrice),
      })),
    subtotal,
    tax: 0,
    total: subtotal,
    memo,
    showSignature,
    showPayLink,
    showCardFee,
    showValidFor,
    showLateFee,
    isLumpSum,
    showQtyCol,
    showUnitPriceCol,
    validUntil: dueDate, // payment due date (invoice date + N days)
    invoiceDate: invoiceDate,
    status,
  });

  // Push the generated PDF into the project's AccountsReceivable/Invoices
  // Dropbox folder. Overwrites on re-save (same invoice number = same file).
  const uploadInvoicePDF = async (number, token) => {
    try {
      const doc = buildPDF();
      const pdfBase64 = doc.output('datauristring'); // data:application/pdf;base64,...
      await axios.post(
        `${API_BASE}/invoices/upload-pdf`,
        { projectId: selectedProject._id, number, pdfBase64 },
        { headers: { Authorization: token } }
      );
    } catch (err) {
      console.error('Dropbox PDF upload failed:', err);
      alert('Invoice was saved, but uploading the PDF to Dropbox failed: ' + (err.response?.data?.error || err.message));
    }
  };

  // Create the first time, UPDATE every time after — so Save followed by
  // Save & Send never produces a duplicate. Returns the saved invoice, or null.
  const persistInvoice = async (status) => {
    if (sentLocked) { alert('This invoice has been sent and is view-only. Delete it and create a new one to make changes.'); return null; }
    if (!selectedProject) { alert('Please select a project'); return null; }
    // Project has no REAL customer record. If a name was typed via the card's
    // Edit, create the customer for real and attach it to the project; if not
    // even a name exists, say so plainly instead of crashing on customer._id.
    let newCustomerId = null;
    if (!selectedProject.customer?._id) {
      const typedName = (selectedProject.customer?.name || '').trim();
      if (!typedName) {
        alert(`"${selectedProject.title || 'This project'}" has no customer attached. Click Edit on the Selected card and type a customer name, or attach a customer to the project.`);
        return null;
      }
      try {
        const token = localStorage.getItem('token');
        // DUPLICATE-PROOF: look for an existing customer with this name first.
        const allCust = await axios.get(`${API_BASE}/customers`, { headers: { Authorization: token } });
        const existing = (allCust.data || []).find(c => (c.name || '').trim().toLowerCase() === typedName.toLowerCase());
        let customer;
        if (existing) {
          if (!window.confirm(`"${typedName}" is already a customer. Attach the existing ${typedName} to this project and invoice them?`)) return null;
          customer = existing;
        } else {
          if (!window.confirm(`"${typedName}" isn't in your customers yet. Create them as a new customer and attach to this project?`)) return null;
          const custRes = await axios.post(`${API_BASE}/customers`, {
            name: typedName,
            businessName: selectedProject.customer?.businessName || '',
            primaryPhone: selectedProject.customer?.primaryPhone || '',
            primaryEmail: selectedProject.customer?.primaryEmail || '',
            status: 'Active',
          }, { headers: { Authorization: token } });
          customer = custRes.data;
        }
        newCustomerId = customer._id;
        // Attach to the project so next time it's just there.
        await axios.put(`${API_BASE}/projects/${selectedProject._id}`, { customer: newCustomerId }, { headers: { Authorization: token } });
        setSelectedProject({ ...selectedProject, customer });
      } catch (err) {
        alert('Could not attach a customer: ' + (err.response?.data?.error || err.message));
        return null;
      }
    }
    if (lineItems.every((i) => !i.description.trim())) { alert('Add at least one line item'); return null; }
    if (isSaving) return null;
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = buildPayload(currentInvoiceId ? savedStatus : status, newCustomerId); // updates keep their status
      let saved;
      if (currentInvoiceId) {
        const res = await axios.put(`${API_BASE}/invoices/${currentInvoiceId}`, payload, { headers: { Authorization: token } });
        saved = res.data;
      } else {
        const res = await axios.post(`${API_BASE}/invoices`, payload, { headers: { Authorization: token } });
        saved = res.data;
        setCurrentInvoiceId(saved._id);
        setSavedNumber(saved.number);
        // Keep the project's invoice list current so a later Clear → new
        // invoice correctly numbers the next variant (-002, -003…).
        setProjectInvoices((prev) => [...prev, saved]);
      }
      await uploadInvoicePDF(saved.number, token);
      return saved;
    } catch (err) {
      alert('Failed to save invoice: ' + (err.response?.data?.error || err.message));
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  // Revise a SENT invoice: accounting-honest editing. The original keeps its
  // number but flips to Revised (void — struck in Revenue, excluded from
  // totals); the edits save as {base}R{n}, a fresh Draft you can adjust,
  // re-save, and send. Same convention quotes use.
  const handleRevise = async () => {
    if (!currentInvoiceId || !savedNumber || isSaving) return;
    const base = savedNumber.replace(/R\d+$/, '');
    const revCount = projectInvoices.filter((i) => (i.number || '').replace(/R\d+$/, '') === base).length;
    const newNumber = `${base}R${revCount}`;
    if (!window.confirm(`Create revision ${newNumber}? ${savedNumber} becomes Revised (void) and stops counting toward totals.`)) return;
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...buildPayload('Draft'), number: newNumber, ...savedSources };
      const res = await axios.post(`${API_BASE}/invoices`, payload, { headers: { Authorization: token } });
      await axios.put(`${API_BASE}/invoices/${currentInvoiceId}`, { status: 'Revised' }, { headers: { Authorization: token } });
      setProjectInvoices((prev) => [...prev, res.data]);
      setCurrentInvoiceId(res.data._id);
      setSavedNumber(res.data.number);
      setSavedStatus('Draft'); // the revision is editable until it's sent
      await uploadInvoicePDF(res.data.number, token);
      setToastMessage(`Revision ${res.data.number} created — ${savedNumber} is now void.`);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 4000);
    } catch (err) {
      alert('Could not create the revision: ' + (err.response?.data?.error || err.message));
    } finally { setIsSaving(false); }
  };

  // Save button: persist the record + upload the PDF to Dropbox. No email.
  const handleSave = async () => {
    const saved = await persistInvoice('Draft');
    if (!saved) return;
    setToastMessage(`Saved ${saved.number} to Dropbox (Invoices)`);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  // Send button: ONLY creates the Outlook draft (PDF attached). It does NOT save
  // to the database or Dropbox — that's strictly the Save button's job.
  // Send-button stage: '' | 'pdf' | 'draft' — narrates the multi-second wait.
  const [sendStage, setSendStage] = useState('');
  const handleSaveAndSend = async () => {
    if (!selectedProject) return alert('Please select a project');
    if (lineItems.every((i) => !i.description.trim())) return alert('Add at least one line item');
    if (sendStage) return; // already in flight
    try {
      // Already sent → this is a re-send of the existing record: draft only.
      if (sentLocked) { await createOutlookDraft({ number: invoiceNumber }); return; }
      // Ensure the record exists, create the Outlook draft, then mark it Sent
      // so the Revenue tab reflects reality (backend stamps sentAt).
      let invId = currentInvoiceId;
      if (!invId) {
        const saved = await persistInvoice('Draft');
        if (!saved) return;
        invId = saved._id;
      }
      await createOutlookDraft({ number: invoiceNumber });
      await axios.put(`${API_BASE}/invoices/${invId}`, { status: 'Sent' }, { headers: { Authorization: localStorage.getItem('token') } });
      setSavedStatus('Sent'); // locks the form — view-only from here
    } catch (e) {
      alert('Send failed: ' + (e.response?.data?.error || e.message));
    } finally { setSendStage(''); }
  };

  // Creates a DRAFT in your Microsoft 365 Outlook mailbox with the invoice PDF
  // already attached (via Microsoft Graph, server-side). The draft syncs to your
  // Outlook desktop app within seconds — you open it, review, and click Send.
  // Nothing is sent automatically.
  const createOutlookDraft = async (saved) => {
    // Fetch the LATEST customer at send time — the page's copy is a snapshot
    // from page load (or a restored draft) and goes stale if the customer was
    // edited since (name, email, etc.).
    let customer = billToCust || selectedProject?.customer || {};
    const custId = customer._id || (typeof customer === 'string' ? customer : null);
    if (custId) {
      try {
        const { data } = await axios.get(`${API_BASE}/customers/${custId}`, { headers: { Authorization: localStorage.getItem('token') } });
        if (data) customer = data;
      } catch (_) { /* fall back to the snapshot */ }
    }
    const email = customer.primaryEmail || customer.altEmail || '';
    const firstName = customer.firstName || (customer.name || '').trim().split(/\s+/)[0] || 'there';

    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

    const subject = `Invoice ${saved.number} — Stalwart Electric`;
    const body =
      `Good ${timeOfDay} ${firstName},\n\n` +
      `Attached to this email is a copy of Invoice # ${saved.number}. ` +
      `If you have any comments, questions or concerns please contact me.\n\n` +
      `Thank you,`;

    if (!email) alert('This customer has no email on file — the draft will open with an empty "To" field.');

    try {
      const token = localStorage.getItem('token');
      setSendStage('pdf');
      // let the button repaint before the PDF build blocks the main thread
      await new Promise(r => setTimeout(r, 30));
      const doc = buildPDF();
      const pdfBase64 = doc.output('datauristring');
      const filename = `Invoice_${saved.number.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      setSendStage('draft');
      const res = await axios.post(
        `${API_BASE}/invoices/create-outlook-draft`,
        { to: email, subject, body, pdfBase64, filename },
        { headers: { Authorization: token } }
      );
      setToastMessage(`Draft with PDF created in your Outlook Drafts — review & send`);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 4000);
      return res.data;
    } catch (err) {
      alert('Could not create the Outlook draft: ' + (err.response?.data?.error || err.message));
    }
  };

  // Builds and returns the jsPDF document (no download). Used both for the
  // local "Download" and for the Dropbox upload / email attachment flow.
  const buildPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    doc.addImage('/media/Logo_hires.png', 'PNG', 170, 10, 25, 25);
    // INVOICE title at the very top (matches the live preview)
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('INVOICE', 15, 20);
    // Invoice # / Date / Due Date, right beneath the title
    let y = 30;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice # ${invoiceNumber}`, 15, y); y += 5;
    // Parse the YYYY-MM-DD input as a LOCAL date (same fix as the due date):
    // new Date('YYYY-MM-DD') is UTC midnight, which prints as the PREVIOUS
    // day in Pacific time — the invoice looked a day off.
    const invParts = String(invoiceDate || '').split('-');
    const invDateLocal = invParts.length === 3
      ? new Date(Number(invParts[0]), Number(invParts[1]) - 1, Number(invParts[2]))
      : new Date();
    doc.text(`Date: ${invDateLocal.toLocaleDateString()}`, 15, y); y += 5;
    if (showValidFor) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Due Date: ${dueDateStr}`, 15, y); y += 5;
      doc.setFont('helvetica', 'normal');
    }
    const headerY = y + 8;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('STALWART ELECTRIC', 15, headerY);
    doc.text('INVOICE TO:', 85, headerY);
    doc.text('PROJECT ADDRESS:', 195, headerY, { align: 'right' });
    const contentY = headerY + 6;
    const MLH = 4.5; // even line height for all three address columns
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    let leftY = contentY;
    doc.text('4712 Solano Way', 15, leftY); leftY += MLH;
    doc.text('Fair Oaks, CA 95628', 15, leftY); leftY += MLH;
    doc.text('United States', 15, leftY); leftY += MLH;
    doc.text('CSLB Lic. #1109509 · C-10', 15, leftY); leftY += MLH; // license on all contract paperwork
    let middleY = contentY;
    const iCust = billToCust || selectedProject?.customer || {};
    doc.setFontSize(12);
    // Business name + Attn only when customer has a business AND this project
    // bills the business (billTo !== 'Individual').
    if (iCust.businessName && selectedProject?.billTo !== 'Individual') {
      doc.text(iCust.businessName, 85, middleY); middleY += MLH;
      doc.setFontSize(11);
      doc.text(`Attn: ${iCust.name || ''}`.trim(), 85, middleY); middleY += MLH;
    } else {
      doc.text(iCust.name || 'Client Name', 85, middleY); middleY += MLH;
    }
    doc.setFontSize(11);
    const billing = billingAddressFor(iCust, orgAddrs);
    let billingLine = '';
    if (billing.street) billingLine += billing.street + ', ';
    if (billing.city) billingLine += billing.city + ', ';
    if (billing.state) billingLine += billing.state + ' ';
    if (billing.zip) billingLine += billing.zip;
    if (billing.country && billing.country !== 'USA') billingLine += ', ' + billing.country;
    if (billingLine.trim()) {
      const addressLines = doc.splitTextToSize(billingLine.trim(), 50);
      addressLines.forEach((line) => { doc.text(line, 85, middleY); middleY += MLH; });
    }
    if (selectedProject?.customer?.primaryPhone) {
      doc.text(fmtPhone(selectedProject.customer?.primaryPhone), 85, middleY); middleY += MLH;
    }
    let rightY = contentY;
    const job = selectedProject?.jobSiteAddress || {};
    let jobLine = '';
    if (job.street) jobLine += job.street + ', ';
    if (job.city) jobLine += job.city + ', ';
    if (job.state) jobLine += job.state + ' ';
    if (job.zip) jobLine += job.zip;
    if (job.country && job.country !== 'USA') jobLine += ', ' + job.country;
    else if (job.zip) jobLine += ', United States';
    if (jobLine.trim()) {
      const addressLines = doc.splitTextToSize(jobLine.trim(), 55);
      addressLines.forEach((line) => { doc.text(line, 195, rightY, { align: 'right' }); rightY += MLH; });
    } else {
      doc.text('No project address on file', 195, rightY, { align: 'right' });
      rightY += MLH;
    }
    y = Math.max(leftY, middleY, rightY) + 2;
    // Output columns are toggled in the form: Description + Amount always show;
    // Qty and Unit Price appear only when their checkbox is on. Column widths
    // adapt so the table always spans the full 180mm (15mm margins).
    const filledItems = lineItems.filter((i) => i.description.trim());
    const tableColumn = ['Description'];
    if (showQtyCol) tableColumn.push('Qty');
    if (showUnitPriceCol) tableColumn.push('Unit Price');
    tableColumn.push('Amount');
    const tableRows = filledItems.map((item) => {
      const row = [item.description];
      if (showQtyCol) row.push(item.quantity);
      // Lump sum: per-line dollar figures print as '-' (only the total shows).
      if (showUnitPriceCol) row.push(isLumpSum ? '-' : `$${formatPrice(item.unitPrice)}`);
      row.push(isLumpSum ? '-' : `$${formatPrice(Number(item.quantity) * Number(item.unitPrice))}`);
      return row;
    });
    const descW = 180 - 30 - (showQtyCol ? 20 : 0) - (showUnitPriceCol ? 30 : 0);
    const pdfColumnStyles = {};
    let ci = 0;
    pdfColumnStyles[ci++] = { cellWidth: descW };
    if (showQtyCol) pdfColumnStyles[ci++] = { cellWidth: 20, halign: 'right' };
    if (showUnitPriceCol) pdfColumnStyles[ci++] = { cellWidth: 30, halign: 'right' };
    pdfColumnStyles[ci] = { cellWidth: 30, halign: 'right', fontStyle: 'bold' };
    // Precompute footer + totals height so the table reserves room for them and
    // auto-paginates (continues on a 2nd page) instead of colliding.
    const pageH = doc.internal.pageSize.getHeight();
    const memoLinesPdf = (memo && memo.trim()) ? doc.splitTextToSize(memo, 170) : [];
    doc.setFontSize(10);
    // The late-fee note references "the due date", so it only prints when the
    // due date itself is shown — hiding the due date hides the note too.
    const lateFeeVisible = showLateFee && showValidFor;
    const lateLinesPdf = lateFeeVisible ? doc.splitTextToSize(LATE_FEE_NOTE, 180) : [];
    let footerH = 8;
    if (showSignature) footerH += 11;
    if (showThankYou) footerH += 6;
    if (showPayLink) footerH += 11;
    if (lateFeeVisible) footerH += lateLinesPdf.length * 4.6;
    const totalsBlockH = 12 + (showCardFee ? 6 : 0) + (memoLinesPdf.length ? memoLinesPdf.length * 5 + 8 : 0);

    doc.autoTable({
      startY: y,
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [239, 70, 50], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, cellPadding: { top: 2.6, bottom: 2.6, left: 4, right: 4 }, lineWidth: 0.1, lineColor: [239, 70, 50] },
      styles: { fontSize: 9, cellPadding: { top: 2.4, bottom: 2.4, left: 4, right: 4 }, overflow: 'linebreak', lineColor: [224, 224, 224], lineWidth: 0.1, textColor: [45, 45, 45] },
      margin: { left: 15, right: 15, bottom: 14 },
      columnStyles: pdfColumnStyles,
    });

    // The table now fills each page down to a normal bottom margin. The totals
    // sit right under the table when there's room before the footer; otherwise
    // they (and the footer) move to a fresh page, pinned above the footer.
    const footerTop = pageH - footerH;
    const finalY = doc.lastAutoTable.finalY;
    let fy;
    if (finalY + 11 + totalsBlockH + 9 > footerTop) {
      doc.addPage();
      fy = footerTop - totalsBlockH - 9;
    } else {
      fy = finalY + 11;
    }
    doc.setTextColor(45, 45, 45);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Subtotal: $${formatPrice(subtotal)}`, 195, fy, { align: 'right' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(`Total (${currency}): $${formatPrice(subtotal)}`, 195, fy + 7, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    if (showCardFee) {
      // The merchant portal ADDS the surcharge at checkout — so this is a
      // disclosure, never an amount to type in. (Printing a grossed-up
      // "pay this" number once got a customer double-charged the fee.)
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(`Card payments incur a 3.5% processing fee, added at checkout (approx. $${formatPrice(subtotal * 0.035)}).`, 195, fy + 13, { align: 'right' });
      doc.setTextColor(0, 0, 0);
    }
    const memoFy = fy + (showCardFee ? 6 : 0);
    if (memoLinesPdf.length) {
      doc.setFontSize(9.5);
      doc.setTextColor(80, 80, 80);
      doc.text('Memo:', 15, memoFy + 19);
      doc.text(memoLinesPdf, 15, memoFy + 26);
      doc.setTextColor(0, 0, 0);
    }

    // Footer — pinned to the bottom of the (last) page.
    let textY = pageH - footerH;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    if (showSignature) {
      doc.setLineWidth(0.2);
      doc.text('Signature:', 15, textY);
      doc.line(40, textY, 122, textY);
      doc.text('Date:', 128, textY);
      doc.line(140, textY, 195, textY);
      textY += 12;
    }
    doc.setFontSize(10);
    doc.setTextColor(80);
    if (showThankYou) {
      doc.text('We appreciate your business!', 15, textY);
      textY += 6;
    }
    if (showPayLink) {
      doc.text('Please pay at stalwart-electric.com/pay', 15, textY);
      textY += 5;
      doc.setTextColor(120);
      doc.text('A 3.5% processing fee applies to all credit and debit card payments.', 15, textY);
      textY += 7;
      doc.setTextColor(80);
    }
    if (lateFeeVisible) {
      doc.setTextColor(120);
      doc.text(lateLinesPdf, 15, textY);
      doc.setTextColor(80);
    }

    // Page numbers + "continued to next page" note at the bottom of each page —
    // only when the document is multi-page.
    const pageCount = doc.getNumberOfPages();
    if (pageCount > 1) {
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text(`${i} of ${pageCount}`, 200, pageH - 6, { align: 'right' });
        if (i < pageCount) {
          doc.setFont('helvetica', 'italic');
          doc.text(`Invoice # ${invoiceNumber} — continued to next page`, 15, pageH - 6);
          doc.setFont('helvetica', 'normal');
        }
      }
    }

    return doc;
  };

  const generatePDF = () => {
    const doc = buildPDF();
    doc.save(`Invoice_${invoiceNumber.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
  };


  return (
    <div className="bq-page bq-page-i" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#f6f9fc', padding: '24px 40px 0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#32325d' }}>
      {/* Mobile-only layout (desktop untouched): normal page scroll, stacked columns, wrapped actions */}
      <style>{`
        @media (max-width: 820px){
          .bq-page{ padding: 14px 12px !important; }
          .bq-page-i{ height: auto !important; overflow: visible !important; display: block !important; }
          .bq-head{ flex-wrap: wrap !important; gap: 12px !important; }
          .bq-head h1{ font-size: 22px !important; }
          .bq-actions{ flex-wrap: wrap !important; width: 100%; }
          .bq-actions button{ flex: 1 1 45%; padding: 10px 6px !important; font-size: 13px !important; }
          .bq-cols{ display: block !important; }
          .bq-form{ overflow-y: visible !important; padding: 16px !important; }
          .bq-prev{ position: static !important; margin-top: 24px; padding: 12px !important; display: block !important; }
          .bq-prev iframe{ aspect-ratio: 8.5 / 11; height: auto !important; }
          .bq-irow{ grid-template-columns: 16px minmax(0,1fr) 52px 78px 22px !important; gap: 5px !important; }
          .bq-irow input{ font-size: 13px !important; padding: 8px 5px !important; }
          .bq-card input{ display: block; width: 100% !important; max-width: 100% !important; margin: 3px 0 !important; box-sizing: border-box; }
          .bq-card{ font-size: 14px; }
        }
      `}</style>
      {showSuccessToast && (
        <div style={{
          position: 'fixed',
          bottom: '40px',
          right: '40px',
          background: '#10b981',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
          fontWeight: 600,
          fontSize: '16px',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'slideUp 0.3s ease-out'
        }}>
          <span style={{ fontSize: '20px' }}>✓</span> {toastMessage}
        </div>
      )}
      <div className="bq-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600 }}>Create Invoice</h1>
        <div className="bq-actions" style={{ display: 'flex', gap: '12px' }}>
          <button style={{ padding: '10px 20px', border: '1px solid #e6e6e6', borderRadius: '6px', background: 'white', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/billing')}>
            Cancel
          </button>
          <button style={{ padding: '10px 20px', border: '1px solid #ef4632', color: '#ef4632', borderRadius: '6px', background: 'white', cursor: 'pointer', fontWeight: 600 }} onClick={handleClear}>
            Clear
          </button>
          <button onClick={generatePDF} disabled={!selectedProject} title={!selectedProject ? 'Select a project first' : ''} style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: !selectedProject ? 'not-allowed' : 'pointer', opacity: !selectedProject ? 0.5 : 1 }}>
            Download
          </button>
          {savedStatus !== 'Draft' && currentInvoiceId && (
            <button onClick={handleRevise} disabled={isSaving} title="Edit via a new R-numbered revision — the original becomes void"
              style={{ padding: '10px 20px', background: '#d97706', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: isSaving ? 'wait' : 'pointer' }}>
              Save as Revision
            </button>
          )}
          <button onClick={handleSave} disabled={isSaving || !selectedProject || sentLocked} title={sentLocked ? 'Sent — view only. Delete and recreate to make changes.' : (!selectedProject ? 'Select a project first' : '')} style={{ padding: '10px 20px', background: sentLocked ? '#9ca3af' : '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: (isSaving || !selectedProject || sentLocked) ? (isSaving ? 'wait' : 'not-allowed') : 'pointer', opacity: (isSaving || !selectedProject || sentLocked) ? 0.5 : 1 }}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={handleSaveAndSend} disabled={isSaving || !selectedProject || !!sendStage} title={!selectedProject ? 'Select a project first' : ''}
            style={{ padding: '10px 20px', background: '#ef4632', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600,
              cursor: (isSaving || !selectedProject || sendStage) ? (sendStage ? 'wait' : isSaving ? 'wait' : 'not-allowed') : 'pointer',
              opacity: (isSaving || !selectedProject) ? 0.5 : sendStage ? 0.85 : 1,
              animation: sendStage ? 'sendPulse 1.2s ease-in-out infinite' : 'none', minWidth: '80px' }}>
            {sendStage === 'pdf' ? 'Building PDF…' : sendStage === 'draft' ? 'Creating draft…' : isSaving ? 'Saving…' : 'Send'}
          </button>
          <style>{`@keyframes sendPulse { 0%,100% { opacity: 0.85; } 50% { opacity: 0.55; } }`}</style>
        </div>
      </div>

      <div className="bq-cols" style={{ display: 'flex', gap: '32px', flex: 1, minHeight: 0, paddingBottom: '24px' }}>
        {/* Left: Form (scrolls on its own) */}
        <div className="bq-form" style={{ flex: 1, minWidth: 0, overflowY: 'auto', background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', padding: '32px' }}>
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Project</h2>
            <input
              type="text"
              placeholder={loadingProjects ? "Loading projects..." : "Find project by PO, title, or customer…"}
              value={projectSearch}
              onChange={(e) => setProjectSearch(e.target.value)}
              disabled={loadingProjects}
              style={{ width: '100%', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '16px', opacity: loadingProjects ? 0.6 : 1 }}
            />
            
            {fetchError && <div style={{ color: '#ef4632', marginTop: '8px' }}>{fetchError}</div>}
            
            {projectSearch && !loadingProjects && !quickProjOpen && (
              <div style={{ marginTop: '8px', border: '1px solid #ccd0d5', borderRadius: '6px', maxHeight: '240px', overflowY: 'auto', background: 'white' }}>
                {filteredProjects.length === 0 ? (
                  <div style={{ padding: '12px 16px', color: '#888' }}>No matches found</div>
                ) : (
                  filteredProjects.map((p) => (
                    <div
                      key={p._id}
                      onClick={() => {
                        setSelectedProject(p);
                        setBillToCust(null); // new project → default bill-to
                        setProjectSearch('');
                      }}
                      style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                    >
                      <strong>{p.poNumber}</strong> - {p.title} ({p.customer?.name || 'No customer'})
                    </div>
                  ))
                )}
                <div onClick={openQuickProject} style={{ padding: '12px 16px', cursor: 'pointer', color: '#4f46e5', fontWeight: 600, borderTop: '1px solid #f0f0f0', position: 'sticky', bottom: 0, background: 'white' }}>
                  + New project{projectSearch.trim() ? ` “${projectSearch.trim()}”` : ''}
                </div>
              </div>
            )}
            {!quickProjOpen && !loadingProjects && (
              <button type="button" onClick={openQuickProject} style={{ marginTop: '8px', background: 'none', border: 'none', color: '#4f46e5', fontWeight: 600, fontSize: '14px', cursor: 'pointer', padding: 0 }}>
                + New project (and customer) without leaving this page
              </button>
            )}

            {quickProjOpen && (
              <QuickCreateProject
                seedTitle={quickProjSeed}
                projects={projects}
                onDone={(created, list) => { setProjects(list); setSelectedProject(created); setBillToCust(null); setQuickProjOpen(false); }}
                onCancel={() => setQuickProjOpen(false)}
              />
            )}
          </section>

          {/* NEW: Invoice Date Override Field - placed here without changing any other layout */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Invoice Date</h2>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              style={{ width: '240px', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px' }}
            />
          </section>

          {selectedProject && (
            <section className="bq-card" style={{ marginBottom: '40px', background: '#f0f8ff', padding: '16px', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px' }}>Selected Project</h3>
                {localStorage.getItem('role') === 'superadmin' && !cardForm && (
                  <button type="button" onClick={openCardEdit} title="Edit everything this document prints (super admin)"
                    style={{ background: 'none', border: '1px solid #dde3ea', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}>
                    ✎ Edit
                  </button>
                )}
              </div>
              <strong>PO:</strong> {cardForm ? <input value={cardForm.poNumber} onChange={e => setCardForm({ ...cardForm, poNumber: e.target.value })} style={{ ...ci, width: '90px' }} /> : renderCopy('po', selectedProject.poNumber)}<br />
              <strong>Title:</strong> {cardForm ? <input value={cardForm.title} onChange={e => setCardForm({ ...cardForm, title: e.target.value })} style={{ ...ci, width: '320px' }} /> : renderCopy('title', selectedProject.title)}<br />
              <strong>Customer:</strong> {cardForm ? <><input value={cardForm.name} onChange={e => setCardForm({ ...cardForm, name: e.target.value })} style={{ ...ci, width: '180px' }} /><input value={cardForm.businessName} onChange={e => setCardForm({ ...cardForm, businessName: e.target.value })} placeholder="Business (optional)" style={{ ...ci, width: '180px' }} /></> : renderCopy('cust', selectedProject.customer?.name)}<br />
              <strong>Phone:</strong> {cardForm ? <input value={cardForm.phone} onChange={e => setCardForm({ ...cardForm, phone: fmtPhoneInput(e.target.value) })} style={{ ...ci, width: '140px' }} /> : renderCopy('phone', fmtPhone(selectedProject.customer?.primaryPhone))}<br />
              <strong>Email:</strong> {cardForm ? <input value={cardForm.email} onChange={e => setCardForm({ ...cardForm, email: e.target.value })} style={{ ...ci, width: '240px' }} /> : renderCopy('email', selectedProject.customer?.primaryEmail)}<br />
              <strong>Job site:</strong> {cardForm ? <>
                <input value={cardForm.street} onChange={e => setCardForm({ ...cardForm, street: e.target.value })} placeholder="Street" style={{ ...ci, width: '200px' }} />
                <input value={cardForm.city} onChange={e => setCardForm({ ...cardForm, city: e.target.value })} placeholder="City" style={{ ...ci, width: '110px' }} />
                <input value={cardForm.state} onChange={e => setCardForm({ ...cardForm, state: e.target.value })} placeholder="ST" style={{ ...ci, width: '42px' }} />
                <input value={cardForm.zip} onChange={e => setCardForm({ ...cardForm, zip: e.target.value })} placeholder="ZIP" style={{ ...ci, width: '64px' }} />
              </> : renderCopy('site', selectedProject.jobSiteAddress?.street ? `${selectedProject.jobSiteAddress.street}, ${selectedProject.jobSiteAddress.city || ''} ${selectedProject.jobSiteAddress.state || ''} ${selectedProject.jobSiteAddress.zip || ''}`.replace(/\s+/g, ' ').trim() : '')}<br />
              <strong>Invoice #:</strong> {cardForm ? <input value={cardForm.number} onChange={e => setCardForm({ ...cardForm, number: e.target.value })} style={{ ...ci, width: '170px' }} /> : renderCopy('num', invoiceNumber)}<br />
              {loadingInvoices && <span style={{ color: '#d97706' }}> (calculating next number...)</span>}
              {cardForm && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', maxWidth: '360px' }}>
                  <button type="button" onClick={() => setCardForm(null)} style={{ flex: 1, padding: '8px', background: '#eef0f3', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                  <button type="button" onClick={applyCardEdit} style={{ flex: 1, padding: '8px', background: '#d97706', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}>Apply to this document</button>
                </div>
              )}
            </section>
          )}
          {selectedProject && <BillToPicker project={selectedProject} value={billToCust} onChange={setBillToCust} />}

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Currency</h2>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ width: '240px', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px' }}>
              <option value="USD">USD - United States Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
            </select>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 8px' }}>Items</h2>
            {/* Column header row — Qty / Unit Price checkboxes toggle those columns on the PDF */}
            <div className="bq-irow" style={{ display: 'grid', gridTemplateColumns: '24px 3fr 100px 140px 40px', gap: '12px', marginBottom: '10px', alignItems: 'center', fontSize: '13px', color: '#525f7f' }}>
              <span />
              <span>Description</span>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px', cursor: 'pointer', fontWeight: 600 }}>
                <input type="checkbox" checked={showQtyCol} onChange={(e) => setShowQtyCol(e.target.checked)} style={{ width: '15px', height: '15px' }} /> Qty
              </label>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px', cursor: 'pointer', fontWeight: 600 }}>
                <input type="checkbox" checked={showUnitPriceCol} onChange={(e) => setShowUnitPriceCol(e.target.checked)} style={{ width: '15px', height: '15px' }} /> Unit Price
              </label>
              <span />
            </div>
            {lineItems.map((item, index) => {
              const isDragging = drag && drag.id === item.id;
              const showGapBefore = drag && dropIndex === index
                && dropIndex !== drag.fromIndex && dropIndex !== drag.fromIndex + 1;
              return (
              <React.Fragment key={item.id}>
                {showGapBefore && <DropBar />}
              <div
                ref={(el) => (rowRefs.current[index] = el)}
                className="bq-irow"
                style={{
                  display: isDragging ? 'none' : 'grid', // the dragged row lifts out; a floating copy follows the cursor
                  gridTemplateColumns: '24px 3fr 100px 140px 40px',
                  gap: '12px',
                  marginBottom: '12px',
                  alignItems: 'center',
                }}
              >
                <div
                  onPointerDown={(e) => startRowDrag(e, index)}
                  style={{ cursor: 'grab', color: '#cbd5e1', fontSize: '18px', display: 'flex', justifyContent: 'center', touchAction: 'none' }}
                >
                  ⠿
                </div>
                <input
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                  onKeyDown={(e) => handleDescriptionKeyDown(e, index)}
                  ref={(el) => (descriptionRefs.current[index] = el)}
                  style={{ padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px' }}
                />
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                  style={{ padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', textAlign: 'right' }}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={displayUnitPrice(index, item.unitPrice)}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                      setRawPriceInput(val);
                      const num = val === '' || val === '.' ? 0 : parseFloat(val);
                      updateItem(index, 'unitPrice', isNaN(num) ? 0 : num);
                    }
                  }}
                  onFocus={() => {
                    setFocusedPriceIndex(index);
                    setRawPriceInput(item.unitPrice.toString());
                  }}
                  onBlur={() => {
                    setFocusedPriceIndex(null);
                    setRawPriceInput('');
                  }}
                  placeholder="0.00"
                  style={{ padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', textAlign: 'right', width: '100%' }}
                />
                <button
                  onClick={() => removeItem(index)}
                  style={{ background: 'none', border: 'none', color: '#9b9fa7', fontSize: '20px', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
              </React.Fragment>
              );
            })}
            {/* drop-at-the-very-end indicator */}
            {drag && dropIndex === lineItems.length
              && dropIndex !== drag.fromIndex && dropIndex !== drag.fromIndex + 1 && <DropBar />}
            <button onClick={addLineItem} style={{ color: '#635bff', background: 'none', border: '1px solid #635bff', borderRadius: '8px', padding: '8px 14px', fontSize: '15px', cursor: 'pointer' }}>
              + Add line item
            </button>
            {projectInformalEstimates.length > 0 && (
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  onClick={() => setShowIePicker(v => !v)}
                  style={{ color: '#635bff', background: 'none', border: '1px solid #635bff', borderRadius: '8px', padding: '8px 14px', fontSize: '15px', cursor: 'pointer', marginLeft: '16px' }}
                >
                  + Add item from Informal Estimates
                </button>
                {showIePicker && (
                  <>
                    <span onClick={() => setShowIePicker(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                    <div style={{ position: 'absolute', bottom: '28px', left: '16px', zIndex: 50, background: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', minWidth: '320px', maxHeight: '260px', overflowY: 'auto' }}>
                      {projectInformalEstimates.map(ie => {
                        // Not clickable if already on the document OR already fully
                        // paid (paid work has nothing left to bill).
                        const disabled = addedIeIds.includes(ie._id) || ie.fullyPaid;
                        const tag = addedIeIds.includes(ie._id) ? ' (added)' : ie.fullyPaid ? ' (fully paid)' : '';
                        return (
                        <div
                          key={ie._id}
                          onClick={() => { if (!disabled) addItemFromInformalEstimate(ie); }}
                          style={{ padding: '10px 14px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.75 : 1, borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', gap: '12px', color: disabled ? '#6b7280' : '#32325d' }}
                          onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = '#f6f9fc'; }}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                          <span style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ie.description}{tag}
                          </span>
                          <strong style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>${Number(ie.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                        </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </span>
            )}
            {/* Floating copy of the row that follows the cursor (no native ghost = no snap-back) */}
            {drag && pointer && (() => {
              const di = lineItems.find((i) => i.id === drag.id) || {};
              return (
                <div style={{
                  position: 'fixed', left: pointer.x - drag.dx, top: pointer.y - drag.dy,
                  width: drag.w, height: drag.h, pointerEvents: 'none', zIndex: 1000,
                  display: 'grid', gridTemplateColumns: '24px 3fr 100px 140px 40px',
                  gap: '12px', alignItems: 'center', background: 'white',
                  boxShadow: '0 10px 28px rgba(0,0,0,0.20)', borderRadius: '8px', opacity: 0.97,
                }}>
                  <div style={{ color: '#a3aab8', fontSize: '18px', display: 'flex', justifyContent: 'center' }}>⠿</div>
                  <div style={{ padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{di.description || 'Item'}</div>
                  <div style={{ padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', textAlign: 'right' }}>{di.quantity}</div>
                  <div style={{ padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', textAlign: 'right' }}>${formatPrice(di.unitPrice)}</div>
                  <div />
                </div>
              );
            })()}
          </section>

          <section style={{ marginBottom: '40px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Lump-sum: hide each line's dollar figures on the PDF; only the
                grand total prints. Default ON. */}
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isLumpSum}
                onChange={(e) => setIsLumpSum(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Hide individual amounts (Lump Sum Invoice)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showSignature}
                onChange={(e) => setShowSignature(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show signature
            </label>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showPayLink}
                onChange={(e) => setShowPayLink(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show pay link
            </label>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showCardFee}
                onChange={(e) => setShowCardFee(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show card-payment total (+3.5% processing fee)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showValidFor}
                onChange={(e) => setShowValidFor(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show Due Date
            </label>
            {/* The note references "the due date", so it needs the due date shown. */}
            <label
              title={!showValidFor ? 'Turn on "Show Due Date" first — the note references it' : undefined}
              style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: showValidFor ? 'pointer' : 'not-allowed', opacity: showValidFor ? 1 : 0.45 }}
            >
              <input
                type="checkbox"
                checked={showLateFee && showValidFor}
                disabled={!showValidFor}
                onChange={(e) => setShowLateFee(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show late-payment fee note
            </label>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showThankYou}
                onChange={(e) => setShowThankYou(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show "We appreciate your business!"
            </label>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Payment Due</h2>
            <input
              type="number"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              min="0"
              style={{ width: '120px', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px' }}
            /> days after invoice date
            <div style={{ marginTop: '8px', color: '#525f7f', fontSize: '14px' }}>
              Due Date: <strong>{dueDateStr}</strong>
            </div>
          </section>

          <section>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Memo</h2>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="Optional notes..."
              style={{ width: '100%', height: '100px', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px' }}
            />
          </section>
        </div>

        {/* Right: Preview — fills its column; always fits, never needs page scroll */}
        <div className="bq-prev" style={{ flex: 1, minWidth: 0, minHeight: 0, background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', padding: '20px', display: 'flex', flexDirection: 'column' }}>
          {/* Hidden preload so jsPDF always has the logo cached for the live render */}
          <img src="/media/Logo_hires.png" alt="" style={{ display: 'none' }} />
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px' }}>Live Preview</h2>
          {livePdfUrl ? (
            <iframe
              src={`${livePdfUrl}#toolbar=0&navpanes=0&view=FitH`}
              title="Live invoice PDF"
              style={{ width: '100%', flex: 1, minHeight: 0, border: '1px solid #ddd', borderRadius: '8px' }}
            />
          ) : (
            <div style={{ width: '100%', flex: 1, minHeight: 0, border: '1px dashed #ccd0d5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9b9fa7', fontSize: '15px', textAlign: 'center', padding: '24px' }}>
              Select a project to see the live invoice preview.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateInvoice;